// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import { getProperty } from 'dot-prop';
import * as jsonDiffPatch from 'jsondiffpatch';
import buildDependencies from '../engine/dependencies';

import type { WorkflowState } from './state';
import { ActionType } from '../engine/constants';
import {
  getTagActionsByOutcomeReason,
  isActionOfType,
} from '../../service/actions';
import type {
  Action,
  Expression,
  ExpressionTerm,
  PublishedAttribute,
  SchemaInstance,
  Tag,
  WorkflowInfo,
  WorkflowProps,
} from '../engine/types';
import { WEBHOOK_KEY } from 'flow/service/actionOptions';
import type { WorkflowSnapshots } from './types';
import { createSnapshot } from 'flow/service/history';
import { isOutcomeEphemeral } from 'flow/service/outcomes';

function getWorkflowsByApplicationId(allWorkflows: WorkflowInfo[]) {
  return allWorkflows.reduce((all, workflow) => ({
    ...all,
    [workflow.application_id]: workflow,
  }), {} as Record<string, WorkflowInfo>);
}

// remove configurations that are not available to the current customer, as imports can occur across customers
export function sanitizeImportedActions(
  importedActions: Record<string, Action>,
  allCustomerWorkflows: WorkflowInfo[],
) {
  const sanitizedActions: Record<string, Action> = {};
  const allWorkflowsByAppId = getWorkflowsByApplicationId(allCustomerWorkflows);

  Object.entries(importedActions).forEach(([actionId, action]) => {
    sanitizedActions[actionId] = structuredClone(action);

    if (isActionOfType(action, ActionType.SERVICE)) {
      // overwrite the service options object to ensure that we
      // do not add invalid recurring workflow configurations
      sanitizedActions[actionId].options = {};

      Object.entries(importedActions[actionId].options).forEach(([optionId, option]) => {
        if (optionId === WEBHOOK_KEY) {
          const { value } = option;

          if (allWorkflowsByAppId[Number(value)]) {
            sanitizedActions[actionId].options[WEBHOOK_KEY] = structuredClone(option);
          }
        } else {
          sanitizedActions[actionId].options[optionId] = structuredClone(option);
        }
      });
    }
  });
  return sanitizedActions;
}

export function snapshotAndCloneWorkflowData(state: WorkflowState) {
  const workflowData = cloneDeep(state.workflowData);
  const workflowSnapshots: WorkflowSnapshots = {
    ...state.workflowSnapshots,
    workflowData: {
      history: {
        initial: state.workflowSnapshots.workflowData.history.initial,
        past: [
          // Only store the last 100 snapshots for performance reasons
          ...state.workflowSnapshots.workflowData.history.past.slice(-100),
          createSnapshot({ data: cloneDeep(state.workflowData) }),
        ],
      },
    },
  };

  return { workflowData, workflowSnapshots };
}


export function compileWorkflowDataDependencies(workflowData: WorkflowState['workflowData']) {
  return {
    ...workflowData,
    dependencies: buildDependencies({
      actions: workflowData.actions,
      dataRoutes: workflowData.dataRoutes,
      thresholds: workflowData.thresholds,
    }),
  };
}

/**
 * Get parent expression item by generating a new path that excludes the last index in the original path string
 */
export function getParentPathAndExpressionItem({
  indices,
  existingExpression,
}: {
  indices: string[];
  existingExpression: Expression;
}): {
    pathWithoutLastIndex: string;
    parentExpressionArrayOfTheExpressionOnTheLastIndex: (ExpressionTerm | ExpressionTerm[])[];
  } {
  if (indices.length === 1) {
    return {
      pathWithoutLastIndex: '',
      parentExpressionArrayOfTheExpressionOnTheLastIndex: [],
    };
  }

  // e.g. original path: [0][1] -> new path: [0]
  const pathWithoutLastIndex = indices.slice(0, Number(indices.length - 1))
    .reduce((result, indexValue) => `${result}[${indexValue}]`, '');
  // we get the parent expression array using that new path
  const parentExpressionArrayOfTheExpressionOnTheLastIndex = getProperty(
    existingExpression, pathWithoutLastIndex) as ExpressionTerm[];

  return {
    pathWithoutLastIndex,
    parentExpressionArrayOfTheExpressionOnTheLastIndex,
  };
}

export function removeEphemeralOutcomeFromTags({
  actions,
  tags,
  outcomeName,
}: {
  actions: Partial<SchemaInstance>['actions'];
  tags: Tag[];
  outcomeName: string;
}): {
    updatedActions: Partial<SchemaInstance>['actions'];
    updatedTags: Tag[];
  } {
  const relatedTagOutcomeActions = getTagActionsByOutcomeReason(actions, outcomeName);

  if (relatedTagOutcomeActions.length === 0) {
    return {
      updatedActions: actions,
      updatedTags: tags,
    };
  }

  const updatedActions = { ...actions };
  const updatedTags = [...tags];

  relatedTagOutcomeActions.forEach(tagAction => {
    const updatedTagAction = {
      ...tagAction,
      tag: {
        ...tagAction.tag,
        outcomes: tagAction.tag.outcomes
          .filter(outcome => !(outcome.isEphemeral && outcome.outcome === outcomeName)),
      },
    };

    const matchingTagProp = tags
      .find(tag => tag.tag_name === tagAction.tag.name);

    if (matchingTagProp) {
      const updatedTagProp = {
        ...matchingTagProp,
        outcomes: matchingTagProp.outcomes
          .filter(outcome => !(isOutcomeEphemeral(outcome)
        && outcome.outcome === outcomeName)),
      };

      updatedActions[tagAction.publicId] = updatedTagAction;
      const tagIndex = updatedTags.findIndex(tag => tag.tag_name === matchingTagProp.tag_name);
      updatedTags[tagIndex] = updatedTagProp;
    }
  });


  return { updatedActions, updatedTags };
}
/**
 * This is unused for now but will be once we support all account-level elements that
 * update an Action
 * todo BUILD-817
 */
export function computeActionsToUpdateWithNewProps(
  actions: WorkflowState['workflowData']['actions'],
  previousProps: WorkflowProps,
  nextProps: Partial<WorkflowProps>,
) {
  const nextActions = cloneDeep(actions);

  const previousPAs = previousProps.publishedAttributes;
  const nextPAs = nextProps.publishedAttributes;
  // only PAs are supported for now - the diffing will grow to support tags, outcomes, and services
  const diffPatcher = jsonDiffPatch.create({
    objectHash: (pa: PublishedAttribute) => pa.attributeToken,
  });

  const diffResult = diffPatcher.diff(previousPAs, nextPAs);

  const diffByPAToken = Object
    .entries(diffResult)
    .reduce((result, [key, changes]) => {
      const numericKey = Number(key);

      if (!isNaN(numericKey)) {
        const paToken = nextPAs[numericKey].attributeToken;
        result[paToken] = changes;
      }
      return result;
    }, {});


  // maps the incoming PA field names to the equivalent action name
  const publishedAttributePropertyMap = {
    attributeName: 'name',
    dataType: 'dataType',
  };

  Object.entries(diffByPAToken).forEach(([token, changes]) => {
    /**
     * todo - once other elements (tags, etc) are supported, actions should indexed via either
     * their token or some other identifier to avoid this nested loop.
     */
    const action = Object.values(nextActions)
      .find(a => a.input?.publishedAttributeToken === token);

    Object
      .entries(changes)
      .forEach(([field, values]) => {
        if (Object.hasOwn(publishedAttributePropertyMap, field)) {
          // diff changes are in a 2-element array with the old and new value
          const [, newValue] = values;
          const actionKeyName = publishedAttributePropertyMap[field];
          action.input[actionKeyName] = newValue;
        }
      });
  });


  return nextActions;
}
